import * as React from "react"

import { cn } from "@/utils/cn"
import { Button } from "./button"
import { FilterIcon } from "lucide-react"
import { ColumnType } from "@/types/filter"
import { FormikSelect, SelectItem } from "./select"
import { NumberFormatValues, NumericFormat, PatternFormat } from "react-number-format"
import { useFormikContext } from "formik"
import { ReloadIcon } from "@radix-ui/react-icons"
import { Col } from "../grid/col"
import { Label } from "./label"
import { FIELD_ERROR_CLASS, FieldErrorMessage, useFieldError } from "./field-error"
import { FormikInputDate } from "./formik-input"

export interface InputProps
  extends React.InputHTMLAttributes<HTMLInputElement> {}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, type, ...props }, ref) => {
    return (
      <input
        type={type}
        className={cn(
          "flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50",
          className
        )}
        ref={ref}
        {...props}
      />
    )
  }
)
Input.displayName = "Input"

type FormikInputSearchButtonProps = {
  name: string;
  placeholder: string;
  loading?: boolean;
  type: ColumnType;
  handleChange?(value: any): void;
  value?: any;
}

export const FormikInputSearch = ({name, placeholder, loading, type, handleChange, value}: FormikInputSearchButtonProps) => {
  if (type === 'CURRENCY'){
    return (
        <NumericFormat 
            prefix="R$ "
            thousandSeparator="."
            decimalSeparator=","
            decimalScale={2}
            fixedDecimalScale
            customInput={Input} 
            className={'w-full'} 
            placeholder={placeholder}
            name={name}
            onValueChange={(value: NumberFormatValues) => handleChange!(value.value)}
            value={value}
        />
    );
}

if (type === 'NUMBER'){
    return (
        <NumericFormat 
            customInput={Input} 
            className={'w-full'} 
            placeholder={placeholder}
            name={name}
            onValueChange={(values: NumberFormatValues) => handleChange!(values.value)}
            value={value}
        />
    );
}

if (type === 'CPF_CNPJ'){
    return (
        <PatternFormat 
            format={'###.###.###-###'}
            customInput={Input} 
            className="w-full"
            placeholder={placeholder}
            name={name}
            onValueChange={(values: NumberFormatValues) => handleChange!(values.value)}
            value={value}
        />
    );
}

if (type === 'DATE'){
    return (
        <>
        <FormikInputDate name={name} />
        </>
    )
}

if (type === 'BOOLEAN'){
    return (
        <>
        <FormikSelect name={name} classNames={'w-full'} onChange={handleChange} value={value}>
          <SelectItem value={'sim'}>Sim</SelectItem>
          <SelectItem value={'nao'}>Não</SelectItem>
        </FormikSelect>
        </>
    );
}

return(
  <Input 
    className="w-full" 
    name={name} 
    placeholder={placeholder} 
    onChange={(e:any) => handleChange!(e.target.value)}
    value={value} />
);
}

export const FormikInputSearchButton = (props: FormikInputSearchButtonProps) => {
  const {setFieldValue, values} = useFormikContext<any>();
  return (
    <div className="flex w-full items-center gap-2">
      <FormikInputSearch {...props} value={values[props.name]} handleChange={(value: any) => setFieldValue(props.name, value)} />
      <Button type="submit" variant="outline" disabled={props.loading}>
        {props.loading ? (
          <ReloadIcon className="mr-2 h-4 w-4 animate-spin"/>
        ): (
          <FilterIcon className="mr-2 h-4 w-4" />
        )}
        Filtrar
      </Button>
    </div>
  )
}

export interface FormikInputProps extends InputProps {
  icon?: React.ReactNode;
  size?: number;
  label?: string;
  field?: any;
  showError?: boolean;
  loading?: boolean;
  inputType?: 'CPF'|'CNPJ'
}

export const FormikInput = ({showError=true,...props}: FormikInputProps) => {
  const formikProps = useFormikContext<any>();
  const {values, handleChange, handleBlur} = formikProps;
  const name = props.field?.name || props.name;
  const errorMessage = useFieldError(name);
  const error = !!errorMessage;
  const { icon, loading, inputType, label, size, field, ...inputProps } = props;

  var component = (
    <>
    {label && (
      <Label htmlFor={name}>{label}</Label>
    )}
    <div className="relative">
      {icon && !loading && (
        <div className="pointer-events-none absolute right-3 top-1/2 grid h-5 w-5 -translate-y-1/2 place-items-center text-muted-foreground">
          {icon}
        </div>
      )}
      {loading && (
        <div className="absolute right-3 top-1/2 grid h-5 w-5 -translate-y-1/2 place-items-center text-muted-foreground">
          <ReloadIcon className="animate-spin"/>
        </div>
      )}
      {(inputType === 'CPF' || inputType === 'CNPJ') && (
        <PatternFormat 
          format={inputType == 'CNPJ' ? '##.###.###/####-##' : '###.###.###-##'}
          customInput={Input} 
          id={name}
          aria-invalid={error}
          className={cn(error && FIELD_ERROR_CLASS, props.className)}
          placeholder={props.placeholder}
          name={name}
          disabled={props.disabled}
          onBlur={handleBlur}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            handleChange(event);
            props.onChange && props.onChange(event)
          }} 
          value={props.value || values[name]} 
        />
      )}
      {!inputType && (
        <Input
          {...inputProps}
          id={props.id || name}
          name={name}
          aria-invalid={error}
          className={cn(error && FIELD_ERROR_CLASS, props.className)}
          value={props.value || values[name]} 
          onBlur={handleBlur}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            handleChange(event);
            props.onChange && props.onChange(event)
          }} 
          />
      )}
    </div>
    {showError && <FieldErrorMessage message={errorMessage} />}
    </>
  );

  if (props.size){
    return <Col xs={12} sm={props.size}>{component}</Col>
  }


  return component;
}

export { Input }
